import { FormEvent, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { api } from "../../api/client";
import { AlertBanner } from "../../components/feedback/AlertBanner";
import { Field } from '../../components/forms/Field';
import { apiError } from "../../lib/apiError";
import { formatDateTime } from "../../lib/format";
import type { TagVerificationSummary } from "../../types";

async function fetchPdf(sessionId: number): Promise<Blob> {
  const response = await fetch(`/api/reports/tag-verification/${sessionId}.pdf`, {
    credentials: "same-origin",
    headers: { Accept: "application/pdf" },
  });
  if (!response.ok) {
    throw new Error("Nie udało się pobrać raportu PDF.");
  }
  return response.blob();
}

export function ReportsPage() {
  const [searchParams] = useSearchParams();
  const [sessions, setSessions] = useState<TagVerificationSummary[]>([]);
  const [sessionId, setSessionId] = useState(searchParams.get("session") || "");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void (async () => {
      const result = await api.GET("/api/tag-verifications");
      if (result.error) {
        setError(apiError(result.error, "Nie udało się wczytać sesji raportów."));
        return;
      }
      const list = (result.data as TagVerificationSummary[]) ?? [];
      setSessions(list);
      if (!sessionId && list[0]) {
        setSessionId(String(list[0].id));
      }
    })();
  }, [sessionId]);

  const selected = useMemo(
    () => sessions.find((item) => String(item.id) === sessionId) ?? null,
    [sessionId, sessions],
  );

  async function openPdf(event?: FormEvent) {
    event?.preventDefault();
    const id = Number(sessionId);
    if (!id) {
      setError("Wybierz sesję weryfikacji.");
      return;
    }
    setBusy(true);
    try {
      const blob = await fetchPdf(id);
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Nie udało się otworzyć PDF.");
    } finally {
      setBusy(false);
    }
  }

  async function downloadPdf() {
    const id = Number(sessionId);
    if (!id) {
      setError("Wybierz sesję weryfikacji.");
      return;
    }
    setBusy(true);
    try {
      const blob = await fetchPdf(id);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `weryfikacja-kolczykow-${id}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Nie udało się zapisać PDF.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="page" data-testid="reports-page">
      <header className="page__header">
        <p className="page__kicker">Raporty</p>
        <h1>Generowanie raportów</h1>
      </header>
      <AlertBanner variant="error" message={error} onDismiss={() => setError(null)} />
      <form className="panel form" onSubmit={(event) => void openPdf(event)}>
        <Field label="Rodzaj raportu">
          <select className="input" defaultValue="tag-verification">
            <option value="tag-verification">Weryfikacja kolczyków</option>
          </select>
        </Field>
        <Field label="Sesja weryfikacji">
          <select
            className="input"
            value={sessionId}
            onChange={(event) => setSessionId(event.target.value)}
            data-testid="reports-session"
            required
          >
            {sessions.length === 0 ? <option value="">Brak zakończonych sesji</option> : null}
            {sessions.map((item) => (
              <option key={item.id} value={item.id}>
                {item.title} · {formatDateTime(item.completed_at)}
              </option>
            ))}
          </select>
        </Field>
        {selected ? (
          <p className="muted">
            Na liście {selected.total_count}, zweryfikowane {selected.verified_count},
            niezarejestrowane {selected.not_found_count}.
          </p>
        ) : null}
        <div className="actions">
          <button className="btn" type="submit" disabled={busy || !sessionId} data-testid="reports-open-pdf">
            Otwórz raport PDF
          </button>
          <button
            className="btn btn--ghost"
            type="button"
            disabled={busy || !sessionId}
            onClick={() => void downloadPdf()}
          >
            Zapisz lokalnie
          </button>
        </div>
      </form>
    </main>
  );
}
